import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { LogOut, Plus, Rocket, User, Menu, X, Shield, Settings, MessageSquare } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import NotificationBell from "@/components/NotificationBell";
import LanguageSelector from "@/components/LanguageSelector";

const Navbar = () => {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [unreadMsgs, setUnreadMsgs] = useState(0);

  useEffect(() => {
    if (!user) { setIsAdmin(false); setUnreadMsgs(0); return; }
    supabase.rpc("has_role", { _user_id: user.id, _role: "admin" }).then(({ data }) => {
      setIsAdmin(!!data);
    });

    const fetchUnread = async () => {
      const { count } = await supabase
        .from("notifications")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("type", "new_message" as any)
        .eq("is_read", false);
      setUnreadMsgs(count ?? 0);
    };
    void fetchUnread();

    const ch = supabase
      .channel(`navbar-msgs-${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, () => {
        void fetchUnread();
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <nav className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-lg">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2 font-bold text-xl">
          <Rocket className="h-6 w-6 text-primary" />
          <span className="gradient-text">Vitrine SaaS</span>
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-4">
          <Link to="/discover" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            {t("nav.discover")}
          </Link>
          <Link to="/missions" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            {t("nav.missions")}
          </Link>
          <Link to="/promotions" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            {t("nav.promotions")}
          </Link>
          <Link to="/bounties" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            Demandas
          </Link>
          {user ? (
            <>
              {profile?.user_type?.includes("founder") && (
                <Button variant="outline" size="sm" onClick={() => navigate("/products/new")}>
                  <Plus className="h-4 w-4 mr-1" /> {t("nav.registerSaas")}
                </Button>
              )}
              {isAdmin && (
                <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}>
                  <Shield className="h-4 w-4 mr-1" /> {t("nav.admin")}
                </Button>
              )}
              <NotificationBell />
              <Button variant="ghost" size="sm" onClick={() => navigate("/inbox")} aria-label="Mensagens" className="relative">
                <MessageSquare className="h-4 w-4" />
                {unreadMsgs > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
                    {unreadMsgs > 9 ? "9+" : unreadMsgs}
                  </span>
                )}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
                <User className="h-4 w-4 mr-1" />
                {profile?.full_name || t("nav.dashboard")}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate("/settings")}>
                <Settings className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleSignOut}>
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => navigate("/login")} className="text-foreground">
                {t("nav.login")}
              </Button>
              <Button size="sm" onClick={() => navigate("/signup")}>
                {t("nav.signup")}
              </Button>
            </>
          )}
          <LanguageSelector />
        </div>

        {/* Mobile toggle */}
        <div className="md:hidden flex items-center gap-2">
          {user && <NotificationBell />}
          <LanguageSelector />
          <Button variant="outline" size="sm" onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen} className="px-2 text-foreground">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-background p-4 space-y-3">
          <Link to="/discover" className="block text-sm font-medium text-foreground" onClick={() => setMobileOpen(false)}>
            {t("nav.discover")}
          </Link>
          <Link to="/missions" className="block text-sm font-medium text-foreground" onClick={() => setMobileOpen(false)}>
            {t("nav.missions")}
          </Link>
          <Link to="/promotions" className="block text-sm font-medium text-foreground" onClick={() => setMobileOpen(false)}>
            {t("nav.promotions")}
          </Link>
          <Link to="/bounties" className="block text-sm font-medium text-foreground" onClick={() => setMobileOpen(false)}>
            Demandas
          </Link>
          {user ? (
            <>
              {profile?.user_type?.includes("founder") && (
                <Link to="/products/new" className="block text-sm font-medium text-foreground" onClick={() => setMobileOpen(false)}>
                  {t("nav.registerSaas")}
                </Link>
              )}
              {isAdmin && (
                <Link to="/admin" className="block text-sm font-medium text-foreground" onClick={() => setMobileOpen(false)}>
                  {t("nav.admin")}
                </Link>
              )}
              <Link to="/dashboard" className="block text-sm font-medium text-foreground" onClick={() => setMobileOpen(false)}>
                {t("nav.dashboard")}
              </Link>
              <Link to="/inbox" className="flex items-center justify-between text-sm font-medium text-foreground" onClick={() => setMobileOpen(false)}>
                <span>Mensagens</span>
                {unreadMsgs > 0 && (
                  <span className="ml-2 h-5 min-w-5 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
                    {unreadMsgs > 9 ? "9+" : unreadMsgs}
                  </span>
                )}
              </Link>
              <Link to="/settings" className="block text-sm font-medium text-foreground" onClick={() => setMobileOpen(false)}>
                {t("nav.settings")}
              </Link>
              <button className="block text-sm font-medium text-foreground" onClick={handleSignOut}>
                {t("nav.logout")}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="block text-sm font-medium text-foreground" onClick={() => setMobileOpen(false)}>
                {t("nav.login")}
              </Link>
              <Link to="/signup" className="block text-sm font-medium text-foreground" onClick={() => setMobileOpen(false)}>
                {t("nav.signup")}
              </Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
